"use client"

import { ChevronRight, type LucideIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible.js"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar.js"

type NavItem = {
  title: string
  url: string
  icon: LucideIcon
  isActive?: boolean
  items?: {
    title: string
    url: string
  }[]
}

// Does the current pathname fall under this nav item (top-level or any child)?
// Normalises both sides so "activities" and "/activities" match.
function isItemActive(item: NavItem, pathname: string): boolean {
  const norm = (s: string) =>
    ((s.startsWith("/") ? s : `/${s}`).replace(/\/+$/, "") || "/")
  const current = norm(pathname)
  const urls = [item.url, ...(item.items?.map((i) => i.url) ?? [])]
    .filter((u) => u && u !== "#")
    .map(norm)
  return urls.some(
    (u) => u !== "/" && (current === u || current.startsWith(`${u}/`)),
  )
}

export function NavMain({ items }: { items: NavItem[] }) {
  const { pathname } = useLocation()

  // Controlled open state per item. Seeded from isActive, then auto-opens
  // whenever the current route falls under the item. User can still close
  // manually via the chevron — onOpenChange writes back.
  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(items.map((it) => [it.title, !!it.isActive])),
  )

  useEffect(() => {
    setOpenMap((prev) => {
      let changed = false
      const next = { ...prev }
      for (const item of items) {
        if (isItemActive(item, pathname) && !next[item.title]) {
          next[item.title] = true
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [pathname, items])

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Pages</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            open={openMap[item.title] ?? false}
            onOpenChange={(open) =>
              setOpenMap((prev) => ({ ...prev, [item.title]: open }))
            }
          >
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={item.title}
                onClick={() => {
                  if (item.items?.length) {
                    setOpenMap((prev) => ({ ...prev, [item.title]: true }))
                  }
                }}
              >
                <Link to={item.url}>
                  <item.icon />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
              {item.items?.length ? (
                <>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuAction className="data-[state=open]:rotate-90">
                      <ChevronRight />
                      <span className="sr-only">Toggle</span>
                    </SidebarMenuAction>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items?.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton asChild>
                            <Link to={subItem.url}>
                              <span>{subItem.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </>
              ) : null}
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
